import type { Editor } from "@tiptap/react";
import type { Node as ProsemirrorNode, ResolvedPos } from "prosemirror-model";
import type { Selection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import React from "react";

import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";

import { serializeChatTokenFromNode } from "./chatTokenSerialization";

interface MarkdownStorage {
  getMarkdown: () => string;
}

type MarkdownEnabledEditor = Editor & {
  storage: Editor["storage"] & {
    markdown?: MarkdownStorage;
  };
};

const hasMarkdownStorage = (
  editor: Editor,
): editor is MarkdownEnabledEditor => {
  return "markdown" in editor.storage;
};

const serializeNodeForPrompt = (node: ProsemirrorNode) => {
  if (node.type.name === "text") {
    return node.text ?? "";
  }

  if (node.type.name === "hardBreak") {
    return "<br />";
  }

  const serializedToken = serializeChatTokenFromNode(node);
  return serializedToken ?? "";
};

// NOTE (Bobby, 2025-06-30): This function takes the tip tap editor state and builds an easier string to work with both for the llm and for us to parse + render
// components with.
export const getCleanedChatPrompt = (editor: Editor) => {
  if (hasMarkdownStorage(editor)) {
    return editor.storage.markdown?.getMarkdown() ?? "";
  }

  let result = "";
  editor.state.doc.descendants((node) => {
    result += serializeNodeForPrompt(node);
  });

  return result;
};

export const useChatEditorOnUpdate = () => {
  const [atPosition, setAtPosition] = React.useState<number | null>(null);
  const { isContextMenuOpen, dispatchCurrentChat } = useCurrentChatContext();

  return ({ editor }: { editor: Editor }) => {
    const { state } = editor;
    const { anchor } = state.selection;

    // NOTE (Ben, 2025-12-03): Get the character immediately before the cursor using ProseMirror's document.
    // This correctly handles atomic nodes (like product/asset tokens) that don't
    // appear in getText() but do occupy document positions.
    const charBeforeCursor =
      anchor > 1 ? state.doc.textBetween(anchor - 1, anchor, "", "") : "";

    // Check if @ was just typed
    if (charBeforeCursor === "@" && !isContextMenuOpen) {
      setAtPosition(anchor - 1);
      dispatchCurrentChat({ type: "openContextMenu" });
    }

    // Check if @ was deleted from the tracked position
    if (atPosition !== null) {
      const charAtTrackedPosition =
        atPosition >= 0 && atPosition < state.doc.content.size
          ? state.doc.textBetween(atPosition, atPosition + 1, "", "")
          : "";

      if (charAtTrackedPosition !== "@") {
        dispatchCurrentChat({ type: "closeContextMenu" });
        setAtPosition(null);
      }
    }
  };
};

const selectionHasResolvedPos = (
  selection: Selection,
): selection is Selection & { $from: ResolvedPos } => {
  return "$from" in selection;
};

export const chatEditorHandleKeyDown = (onSubmit: () => void) => {
  const { isContextMenuOpen } = useCurrentChatContext();

  return (view: EditorView, event: KeyboardEvent) => {
    const { state } = view;

    const isInNode = (nodeNames: string[]) => {
      if (!selectionHasResolvedPos(state.selection)) {
        return false;
      }
      const { $from } = state.selection;
      for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (nodeNames.includes(node.type.name)) {
          return true;
        }
      }
      return false;
    };

    const isInList = isInNode(["listItem"]);
    const isInCodeBlock = isInNode(["codeBlock"]);

    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmit();
      return true;
    }

    if (event.key === "Enter" && !event.shiftKey && !isContextMenuOpen) {
      // Allow native list/code behavior inside lists or code blocks
      if (isInList || isInCodeBlock) {
        return false;
      }
      event.preventDefault();
      onSubmit();
      return true;
    }

    return false;
  };
};

/**
 * Returns a handler we can pass to tiptap to handle pasting images and videos in chat.
 *
 * Basically just validates the files and calls addFiles if they're valid.
 */
export const chatEditorHandlePaste = (
  addFiles: (files: File[] | null) => void | Promise<void>,
) => {
  return (_view: EditorView, event: ClipboardEvent) => {
    const clipboardData = event.clipboardData;
    if (clipboardData && clipboardData.files.length > 0) {
      const files = Array.from(clipboardData.files);
      const mediaFiles = files.filter((file) => {
        return file.type.startsWith("image/") || file.type.startsWith("video/");
      });

      if (mediaFiles.length > 0) {
        void addFiles(mediaFiles);
        event.preventDefault();
        return true;
      }
    }

    return false;
  };
};
